import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormGroup} from '@angular/forms';

import {DPAMRequestData, DPAMRequestRedemptionDetailsI, GroupAttributeI} from '../../../services/product-setup.service';
import {ProductSetupChangesService} from '../../../../core/settings/product-setup-changes.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, Subscription} from 'rxjs';
import {ValidationService} from '../../../services/validation.service';

@UntilDestroy()
@Component({
  selector: 'app-group-redemption-details',
  templateUrl: './group-redemption-details.component.html',
  styleUrls: ['./group-redemption-details.component.scss']
})
export class GroupRedemptionDetailsComponent implements OnInit, OnChanges, OnDestroy {
  formValues: DPAMRequestRedemptionDetailsI;

  switchSourceCodeMap = {
    redemptionInstructionsCustomer: true,
    redemptionInstructionsStaff: true,
    digitalTerms: true,
    compatibility: true,
    legalDisclaimer: true
  };

  private afterSaveSubscription: Subscription;
  @Input() afterSave: Observable<void>;
  @Input() data = null as DPAMRequestData;
  @Input() redemptionDetails: FormGroup;
  @Input() isShowWarning = false;
  @Input() disableSave = false;
  @Input() groupAttributes: GroupAttributeI[];
  @Input() isActionsDisabled = false;

  @Output() save: EventEmitter<{ editingRequest: DPAMRequestRedemptionDetailsI, editingSection: string }> = new EventEmitter();
  @Output() addSpecificComment: EventEmitter<string> = new EventEmitter();
  @Output() switchCompare: EventEmitter<any> = new EventEmitter();
  @Output() currentForm: EventEmitter<FormGroup> = new EventEmitter();
  @Output() cancel: EventEmitter<any> = new EventEmitter();

  thisComponent: any;
  isEditRedemptionDetails = false;
  hasMaxLengthError = false;

  constructor(private elementRef: ElementRef,
              private uiChangesService: ProductSetupChangesService,
              private validationService: ValidationService) {
  }

  ngOnInit() {
    this.thisComponent = this.elementRef.nativeElement;
    this.uiChangesService.discardChanges()
      .pipe(untilDestroyed(this))
    .subscribe(() => this.discardChanges());
    this.formValues = this.redemptionDetails.value;
    this.redemptionDetails.valueChanges
    .pipe(untilDestroyed(this))
    .subscribe(values => {
      this.checkForFormChanges(values);
      this.checkFormValid();
    });
    setTimeout(() => {
      this.redemptionDetails.controls['overridableRedemptionDetails'].patchValue({
        'redemptionInstructionsCustomer': this.formValues.overridableRedemptionDetails.redemptionInstructionsCustomer,
        'legalDisclaimer': this.formValues.overridableRedemptionDetails.legalDisclaimer,
        'compatibility': this.formValues.overridableRedemptionDetails.compatibility,
        'digitalTerms': this.formValues.overridableRedemptionDetails.digitalTerms
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['data'] && changes['data'].currentValue) || (changes['redemptionDetails'] && changes['redemptionDetails'].currentValue)) {
      this.formValues = this.redemptionDetails.value;
      this.isEditRedemptionDetails = false;
    }
  }
  checkForFormChanges(data: any) {
    this.uiChangesService.hasChanges = this.uiChangesService.compareFormChanges(this.formValues, data);
  }

  discardChanges() {
    if (this.thisComponent.offsetParent != null) {
      this.uiChangesService.applyDiscardedChanges(this.redemptionDetails, this.formValues);
      this.isEditRedemptionDetails = false;
    }
  }

  @HostListener('document:formValuesSaved', ['$event'])
  onFormValuesSaved(e: Event) {
    this.formValues = this.redemptionDetails.value;
    this.uiChangesService.hasChanges = false;
  }

  onSave() {
    this.validateHtml();
    this.save.emit({
      editingRequest: Object.assign({
        redemptionDetails: this.redemptionDetails.value
      }),
      editingSection: 'group-redemption'
    });
    this.currentForm.emit(this.redemptionDetails);
    this.isShowWarning = true;
    this.isEditRedemptionDetails = false;
    this.formValues = this.redemptionDetails.value;
    this.uiChangesService.hasChanges = false;
  }

  showError(controlName: string) {
    return !this.redemptionDetails.get(controlName).valid && this.isShowWarning;
  }

  onAddComment(fieldName: string) {
    const o: any = this.uiChangesService.continueWithoutChanges()
      .pipe(untilDestroyed(this))
      .subscribe(_continue => {
        if (_continue) {
          this.addSpecificComment.emit(fieldName);
        }
        if (o) { o.unsubscribe(); }
      });
  }

  get isEditable() {
    return true;
  }

  ngOnDestroy() {
    this.uiChangesService.hasChanges = false;
    this.afterSaveSubscription.unsubscribe();
  }

  onCancel() {
    if (this.isEditRedemptionDetails) return;
    this.redemptionDetails.patchValue({
      'expiration': this.formValues.expiration,
      'serviceLevel': this.formValues.serviceLevel,
      'productNotes': this.formValues.productNotes,
      'overridableRedemptionDetails': {
        'redemptionInstructionsCustomer': this.formValues.overridableRedemptionDetails.redemptionInstructionsCustomer,
        'redemptionInstructionsStaff': this.formValues.overridableRedemptionDetails.redemptionInstructionsStaff,
        'digitalTerms': this.formValues.overridableRedemptionDetails.digitalTerms,
        'compatibility': this.formValues.overridableRedemptionDetails.compatibility,
        'legalDisclaimer': this.formValues.overridableRedemptionDetails.legalDisclaimer
      }
    });
  }

  private validateHtml(): void {
    const fields = ['redemptionInstructionsCustomer', 'digitalTerms', 'compatibility', 'legalDisclaimer'];
    fields.forEach(field => {
      if (!this.redemptionDetails.controls['overridableRedemptionDetails'].value[field]) return;
      const value = this.redemptionDetails.controls['overridableRedemptionDetails'].value[field]?.replace(/href="(.*?)"/g, x => x.replace(/\s/g, ''));
      this.redemptionDetails.controls['overridableRedemptionDetails'].value[field] = value;
    });
  }

  onEditorEnterPressed($event: KeyboardEvent): any {
    //Prevent other events on enter key press
    $event.preventDefault();
    $event.stopPropagation();
  }

  showWarning(controlName: string) {
    return this.validationService.showWarning(this.redemptionDetails, controlName, this.isShowWarning);
  }

  checkRequired(controlName: string) {
   return  this.validationService.checkRequired(this.redemptionDetails, controlName, this.isEditRedemptionDetails, this.isShowWarning);
  }

  checkMaxLength(controlName: string) {
   return  this.validationService.checkMaxLength(this.redemptionDetails, controlName);
  }

  showMaxLengthErrorMessage(controlName: string) {
   return  this.validationService.showMaxLengthErrorMessage(this.redemptionDetails, controlName);
  }

  checkFormValid() {
    this.hasMaxLengthError = !(this.validationService.isFormValid(this.redemptionDetails)
      && this.validationService.isFormValid(this.overridableRedemptionDetails));
  }

  get overridableRedemptionDetails() {
    return (this.redemptionDetails.get('overridableRedemptionDetails') as FormGroup);
  }
}
